<template>
  <div>
    <div
      v-if="deleteWarnings.length > 0"
      id="delete-warnings-banner"
      class="mb-6 bg-yellow-900/30 border border-yellow-700/60 rounded-lg p-4"
    >
      <p class="text-yellow-300 font-semibold mb-2">{{ $t('workspace.settings.delete.partialErrorTitle') }}</p>
      <ul class="text-sm text-yellow-200 list-disc list-inside space-y-1">
        <li v-for="(w, i) in deleteWarnings" :key="i">
          <span class="font-mono text-xs">{{ w.resource }}</span>: {{ w.message }}
        </li>
      </ul>
      <!-- Text variant with the contrast severity: white label, readable on the yellow
           banner (the link variant is always cyan and ignores severity). -->
      <Button
        id="btn-dismiss-delete-warnings"
        variant="text"
        severity="contrast"
        size="small"
        class="mt-3 select-none"
        :label="$t('common.close')"
        @click="deleteWarnings = []"
      />
    </div>

    <div class="flex justify-between items-center mb-8">
      <h1 class="text-4xl font-bold">{{ $t('bots.list.title') }}</h1>
      <Button id="btn-request-create-bot" severity="primary" @click="openCreateModal">
        <span class="material-icons">add</span>
        {{ $t('bots.list.createButton') }}
      </Button>
    </div>

    <div v-if="loading" class="text-center text-gray-400 py-12">
      <p>{{ $t('bots.list.loadingText') }}</p>
    </div>
    <div v-else-if="bots.length === 0" class="text-center text-gray-400 bg-gray-800 rounded-lg py-12">
      <span class="material-icons text-6xl mb-4">smart_toy</span>
      <h2 class="text-2xl font-semibold mb-2">{{ $t('bots.list.emptyStateTitle') }}</h2>
      <p>{{ $t('bots.list.emptyStateDescription') }}</p>
    </div>
    <div v-else id="bots-grid" class="grid gap-6" style="grid-template-columns: repeat(auto-fill, minmax(320px, 420px))">
      <BotCard
        v-for="bot in bots"
        :key="bot._id"
        :bot="bot"
        :workspaceId="workspaceId"
        :liveChatHref="liveChatHrefs[bot._id] || null"
        @delete="openDeleteModal"
      />
    </div>

    <!-- Create bot. Closing via X / Esc runs closeCreateModal, same as the Cancel button. -->
    <Dialog
      id="create-bot-modal"
      :visible="showCreateModal"
      modal
      :header="$t('bots.createModal.title')"
      :draggable="false"
      :style="{ width: '28rem' }"
      :breakpoints="{ '640px': '92vw' }"
      @update:visible="(visible) => { if (!visible) closeCreateModal(); }"
    >
      <!-- The submit Button lives in the Dialog footer (outside this form), so it
           is tied back to the form with form="create-bot-form"; Enter still submits. -->
      <form id="create-bot-form" class="flex flex-col gap-4" @submit.prevent="handleCreateBot">
        <div>
          <label for="input-bot-name" class="block text-gray-400 mb-2">{{ $t('bots.createModal.nameLabel') }}</label>
          <InputText
            id="input-bot-name"
            v-model="newBotName"
            class="w-full"
            autofocus
            required
          />
        </div>

        <div>
          <!-- Select's id lands on its root <div>, so the label is linked via aria-labelledby -->
          <label id="label-bot-language" class="block text-gray-400 mb-2">{{ $t('bots.createModal.languageLabel') }}</label>
          <Select
            id="input-bot-language"
            v-model="newBotLanguage"
            :options="languageOptions"
            optionLabel="label"
            optionValue="value"
            aria-labelledby="label-bot-language"
            class="w-full"
          />
        </div>

        <div v-if="createError" class="p-3 text-center bg-red-900/50 text-red-400 rounded-lg">
          {{ createError }}
        </div>
      </form>

      <template #footer>
        <Button
          id="btn-cancel-create-bot"
          type="button"
          severity="secondary"
          :label="$t('bots.createModal.cancelButton')"
          @click="closeCreateModal"
        />
        <Button
          id="btn-confirm-create-bot"
          type="submit"
          form="create-bot-form"
          severity="primary"
          :label="$t('bots.createModal.createButton')"
          :loading="isCreating"
          :disabled="isCreating"
        />
      </template>
    </Dialog>

    <!-- Permanent bot deletion. Opens straight away with a spinner while the deletion preview
         loads. Closing via X / Esc runs closeDeleteModal, same as the Cancel button, and (like
         Cancel) is disabled while the deletion is running. Its state is cleared on @after-hide
         so the stats list doesn't collapse while the Dialog fades out. -->
    <Dialog
      id="delete-bot-modal"
      :visible="showDeleteModal"
      modal
      :header="$t('bots.deleteModal.title')"
      :closable="!isDeleting"
      :close-on-escape="!isDeleting"
      :draggable="false"
      :style="{ width: '32rem' }"
      :breakpoints="{ '640px': '92vw' }"
      @update:visible="(visible) => { if (!visible) closeDeleteModal(); }"
      @after-hide="resetDeleteState"
    >
      <p class="text-red-400 text-sm mb-6 font-semibold">{{ $t('bots.deleteModal.message') }}</p>

      <!-- Loading state -->
      <div v-if="isDeletionLoading" class="flex justify-center py-8">
        <ProgressSpinner
          style="width: 2rem; height: 2rem"
          stroke-width="3"
          :aria-label="$t('common.loading')"
        />
      </div>

      <!-- Deletion stats -->
      <div v-else-if="deletionStats" class="bg-gray-900 rounded-lg p-4 mb-6">
        <p class="text-gray-300 text-sm font-semibold mb-3">{{ $t('bots.deleteModal.dataToBeDeleted') }}</p>
        <ul class="text-gray-400 text-sm flex flex-col gap-1">
          <li v-if="deletionStats.botsToDelete > 0" class="flex justify-between">
            <span>{{ $t('bots.deleteModal.bots') }}:</span>
            <span class="font-bold text-red-400">{{ deletionStats.botsToDelete }}</span>
          </li>
          <li v-if="deletionStats.conversations > 0" class="flex justify-between">
            <span>{{ $t('bots.deleteModal.conversations') }}:</span>
            <span class="font-bold text-red-400">{{ deletionStats.conversations }}</span>
          </li>
          <li v-if="deletionStats.messages > 0" class="flex justify-between">
            <span>{{ $t('bots.deleteModal.messages') }}:</span>
            <span class="font-bold text-red-400">{{ deletionStats.messages }}</span>
          </li>
          <li v-if="deletionStats.documents > 0" class="flex justify-between">
            <span>{{ $t('bots.deleteModal.documents') }}:</span>
            <span class="font-bold text-red-400">{{ deletionStats.documents }}</span>
          </li>
          <li v-if="deletionStats.qas > 0" class="flex justify-between">
            <span>{{ $t('bots.deleteModal.qas') }}:</span>
            <span class="font-bold text-red-400">{{ deletionStats.qas }}</span>
          </li>
          <li v-if="deletionStats.botPrompts > 0" class="flex justify-between">
            <span>{{ $t('bots.deleteModal.botPrompts') }}:</span>
            <span class="font-bold text-red-400">{{ deletionStats.botPrompts }}</span>
          </li>
          <li v-if="deletionStats.mcpServers > 0" class="flex justify-between">
            <span>{{ $t('bots.deleteModal.tools') }}:</span>
            <span class="font-bold text-red-400">{{ deletionStats.mcpServers }}</span>
          </li>
          <li v-if="deletionStats.connectors > 0" class="flex justify-between">
            <span>{{ $t('bots.deleteModal.connectors') }}:</span>
            <span class="font-bold text-red-400">{{ deletionStats.connectors }}</span>
          </li>
          <li v-if="deletionStats.analytics > 0" class="flex justify-between">
            <span>{{ $t('bots.deleteModal.analytics') }}:</span>
            <span class="font-bold text-red-400">{{ deletionStats.analytics }}</span>
          </li>
        </ul>
      </div>

      <!-- Confirmation input -->
      <div v-if="!isDeletionLoading">
        <label for="input-delete-confirm-text" class="block text-gray-300 text-sm font-semibold mb-2">
          {{ $t('bots.deleteModal.confirmLabel') }} "{{ botToDeleteName }}"
        </label>
        <div class="flex gap-2 mb-2">
          <InputText
            id="input-delete-confirm-text"
            ref="deleteConfirmInputRef"
            v-model="deletionConfirmText"
            class="flex-1 min-w-0"
            :placeholder="$t('bots.deleteModal.inputPlaceholder')"
            autofocus
          />
          <Button
            id="btn-copy-bot-name"
            severity="secondary"
            v-tooltip.top="$t('bots.deleteModal.copyTooltip')"
            :aria-label="$t('bots.deleteModal.copyTooltip')"
            @click="copyBotNameToClipboard"
          >
            <template #icon>
              <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">content_copy</span>
            </template>
          </Button>
        </div>
        <p class="text-gray-500 text-xs">{{ $t('bots.deleteModal.confirmHint') }}</p>
      </div>

      <template #footer>
        <Button
          id="btn-cancel-delete-bot"
          severity="secondary"
          :label="$t('bots.deleteModal.cancelButton')"
          :disabled="isDeleting"
          @click="closeDeleteModal"
        />
        <Button
          id="btn-confirm-delete-bot"
          severity="danger"
          :label="isDeleting ? $t('bots.deleteModal.deleting') : $t('bots.deleteModal.confirmButton')"
          :disabled="isDeleteButtonDisabled()"
          :loading="isDeleting"
          @click="confirmDelete"
        />
      </template>
    </Dialog>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import api from '../services/api';
import { useI18n } from 'vue-i18n';
import { useToast } from 'primevue/usetoast';
import { copyToClipboard } from '../utils/clipboard';
import BotCard from '../components/BotCard.vue';
import { getWorkspaceLiveChatLinks } from '../services/liveChatApi';
import { liveChatUrl } from '../utils/liveChatUrl';
import { useBotLanguageOptions } from '../composables/useBotLanguageOptions';
const { t } = useI18n();
const toast = useToast();

const route = useRoute();
const workspaceId = computed(() => route.params.workspaceId);

const bots = ref([]);
const loading = ref(true);
const showCreateModal = ref(false);
const newBotName = ref('');
const newBotLanguage = ref('it');
const isCreating = ref(false);
const createError = ref(null);
const deleteWarnings = ref([]);

const { languageOptions } = useBotLanguageOptions();

const showDeleteModal = ref(false);
const botToDeleteId = ref(null);
const botToDeleteName = ref('');
const deletionConfirmText = ref('');
const deletionStats = ref(null);
const isDeletionLoading = ref(false);
const isDeleting = ref(false);
const deleteConfirmInputRef = ref(null);

// Link "Apri Live Chat" per bot: { [botId]: url }. Errore -> nessun link.
const liveChatHrefs = ref({});

const fetchLiveChatLinks = async () => {
  const ws = workspaceId.value;
  try {
    const data = await getWorkspaceLiveChatLinks(ws);
    if (ws !== workspaceId.value) return;
    const hrefs = {};
    for (const [botId, teamId] of Object.entries(data?.links || {})) {
      const href = liveChatUrl(data.handoverUrl, teamId);
      if (href) hrefs[botId] = href;
    }
    liveChatHrefs.value = hrefs;
  } catch (error) {
    liveChatHrefs.value = {};
    Console.error('Link Live Chat non disponibili:', error);
  }
};

const fetchBots = async () => {
  try {
    loading.value = true;
    const response = await api.get(`/workspaces/${workspaceId.value}/bots`);
    bots.value = response.data.data;
    // una sola chiamata dopo il caricamento dei bot, senza bloccare la lista
    fetchLiveChatLinks();
  } catch (error) {
    Console.error('Errore nel caricamento dei bot:', error);
  } finally {
    loading.value = false;
  }
};

onMounted(async () => {
  // Surface deletion warnings forwarded from WorkspaceSettings (one-shot).
  try {
    const raw = sessionStorage.getItem('workspaceDeleteWarnings');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        deleteWarnings.value = parsed;
      }
      sessionStorage.removeItem('workspaceDeleteWarnings');
    }
  } catch { /* ignore */ }

  await fetchBots();
});
watch(workspaceId, (newId, oldId) => { if (newId && newId !== oldId) fetchBots(); });

const openCreateModal = () => {
  newBotName.value = '';
  newBotLanguage.value = 'it';
  createError.value = null;
  showCreateModal.value = true;
};

const closeCreateModal = () => {
  showCreateModal.value = false;
};

const handleCreateBot = async () => {
  if (!newBotName.value.trim()) {
    createError.value = t('bots.createModal.errorEmpty');
    return;
  }
  isCreating.value = true;
  createError.value = null;
  try {
    await api.post(`/workspaces/${workspaceId.value}/bots`, {
      name: newBotName.value,
      defaultLanguage: newBotLanguage.value
    });
    closeCreateModal();
    await fetchBots();
  } catch (error) {
    Console.error('Errore nella creazione del bot:', error);
    if (error.response?.data?.error?.message) {
      createError.value = error.response.data.error.message;
    } else {
      createError.value = t('bots.createModal.errorUnexpected');
    }
  } finally {
    isCreating.value = false;
  }
};

// Opens the Dialog straight away (spinner while the deletion preview loads), then shows the
// stats and the confirmation input. A failed preview closes the Dialog with an error toast.
const openDeleteModal = async (id) => {
  botToDeleteId.value = id;
  botToDeleteName.value = bots.value.find(b => b._id === id)?.name || '';
  deletionConfirmText.value = '';
  deletionStats.value = null;
  isDeleting.value = false;
  isDeletionLoading.value = true;
  showDeleteModal.value = true;

  // Closed, or reopened for another bot, while the preview was loading: the reply is stale.
  const isStale = () => !showDeleteModal.value || botToDeleteId.value !== id;

  try {
    const response = await api.get(`/workspaces/${workspaceId.value}/bots/${id}/deletion-preview`);
    if (isStale()) return;
    deletionStats.value = response.data.data;
    isDeletionLoading.value = false;
    // The input mounts only now, possibly after the Dialog's own autofocus pass ran.
    await nextTick();
    deleteConfirmInputRef.value?.$el?.focus();
  } catch (error) {
    Console.error('Errore nel caricamento preview cancellazione:', error);
    if (isStale()) return;
    closeDeleteModal();
    toast.add({ severity: 'error', summary: t('bots.deleteModal.previewError'), life: 3000 });
  }
};

const closeDeleteModal = () => {
  showDeleteModal.value = false;
};

// Runs on the Dialog's @after-hide (Cancel / X / Esc, a failed preview or a successful delete):
// clearing the state while the Dialog is still fading out would collapse the stats list, blank
// the bot name and re-enable the footer mid-animation. Skipped if the Dialog was reopened
// during the fade (after-hide still fires then).
const resetDeleteState = () => {
  if (showDeleteModal.value) return;
  botToDeleteId.value = null;
  botToDeleteName.value = '';
  deletionConfirmText.value = '';
  deletionStats.value = null;
  isDeletionLoading.value = false;
  isDeleting.value = false;
};

const copyBotNameToClipboard = () => copyToClipboard(botToDeleteName.value);

const isDeleteButtonDisabled = () => {
  return deletionConfirmText.value !== botToDeleteName.value || isDeletionLoading.value || isDeleting.value;
};

const confirmDelete = async () => {
  if (!botToDeleteId.value || deletionConfirmText.value !== botToDeleteName.value) return;

  isDeleting.value = true;
  try {
    await api.delete(`/workspaces/${workspaceId.value}/bots/${botToDeleteId.value}`);
    await fetchBots();
    // isDeleting stays on until resetDeleteState, so the footer keeps its loading state
    // (and can't be clicked again) while the Dialog fades out.
    closeDeleteModal();
  } catch (error) {
    Console.error('Errore nella cancellazione del bot:', error);
    isDeleting.value = false;
  }
};

</script>